// assets/AllCourses/Exercises/CrosswordExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

const BLACK_CELL = "#";

export default function CrosswordExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= NORMALIZE DATA ================= */
  const crosswordData =
    exercise.crosswordData || { grid: [], clues: { across: [], down: [] } };

  const grid = Array.isArray(crosswordData.grid)
    ? crosswordData.grid.map(row =>
        Array.isArray(row)
          ? row.map(c => (typeof c === "string" ? c : ""))
          : []
      )
    : [];

  const gridSize = grid.length;

  const clues = {
    across: Array.isArray(crosswordData.clues?.across)
      ? crosswordData.clues.across
      : [],
    down: Array.isArray(crosswordData.clues?.down)
      ? crosswordData.clues.down
      : [],
  };

  /* ================= STUDENT STATE ================= */
  const emptyUserGrid =
    gridSize > 0
      ? Array.from({ length: gridSize }, () =>
          Array.from({ length: gridSize }, () => "")
        )
      : [];

  const [userGrid, setUserGrid] = useState(emptyUserGrid);
useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);

  useEffect(() => {
    if (!isPreview) return;
    completedRef.current = false;
    setUserGrid(emptyUserGrid);
  }, [exercise._id, isPreview]);

  /* ================= EDIT HELPERS ================= */
  const updateCrossword = next => {
    if (isEditMode) onUpdate("crosswordData", next);
  };

  const createGrid = size => {
    if (!isEditMode) return;
    updateCrossword({
      grid: Array.from({ length: size }, () =>
        Array.from({ length: size }, () => "")
      ),
      clues: { across: [], down: [] },
    });
  };

  const handleCellChange = (r, c, value) => {
    if (!isEditMode) return;

    const letter =
      value === "#"
        ? BLACK_CELL
        : value
            .slice(0, 1)
            .toUpperCase();

    const nextGrid = grid.map((row, ri) =>
      row.map((cell, ci) =>
        ri === r && ci === c ? letter : cell
      )
    );

    updateCrossword({ ...crosswordData, grid: nextGrid });
  };

  /* ================= STUDENT HELPERS ================= */
  const isBlackCell = (r, c) => grid[r]?.[c] === BLACK_CELL;
  const isActiveCell = (r, c) =>
    !isBlackCell(r, c) && grid[r]?.[c] !== "";

  const getClueNumber = (r, c) =>
    clues.across.find(cl => cl.row === r && cl.col === c)
      ?.number ||
    clues.down.find(cl => cl.row === r && cl.col === c)
      ?.number ||
    null;

  const handleStudentChange = (r, c, value) => {
    if (
        isEditMode ||
        showResults ||
        isBlackCell(r, c) ||
        !isActiveCell(r, c)
      )
      return;

    const letter = value.slice(-1).toUpperCase();

    setUserGrid(prev => {
      const next = prev.map((row, ri) =>
        row.map((cell, ci) =>
          ri === r && ci === c ? letter : cell
        )
      );

      const allFilled = grid.every((row, ri) =>
        row.every(
          (cell, ci) =>
            isBlackCell(ri, ci) ||
            cell === "" ||
            next[ri][ci] !== ""
        )
      );

      if (allFilled && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = currentGrid => {
    let allCorrect = true;

    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (!isActiveCell(r, c)) continue;

        const entered = currentGrid[r][c];
        const expected = grid[r][c];

        if (entered.toUpperCase() !== expected.toUpperCase()) {
          allCorrect = false;
        }
      }
    }

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-10">
        {gridSize === 0 && (
          <div className="text-center py-10">
            <Button onClick={() => createGrid(10)}>
              Create 10×10 Crossword Grid
            </Button>
            <p className="mt-2 text-sm text-muted-foreground">
              Use <strong>#</strong> for black cells
            </p>
          </div>
        )}

        {gridSize > 0 && (
          <div className="flex justify-center">
            <div
              className="grid gap-1 bg-muted/50 p-4 rounded-xl"
              style={{
                gridTemplateColumns: `repeat(${gridSize}, 48px)`,
              }}
            >
              {grid.map((row, rIdx) =>
                row.map((cell, cIdx) => (
                  <Input
                    key={`${rIdx}-${cIdx}`}
                    maxLength={1}
                    value={cell === BLACK_CELL ? "#" : cell}
                    onChange={e =>
                      handleCellChange(
                        rIdx,
                        cIdx,
                        e.target.value
                      )
                    }
                    className={cn(
                      "w-12 h-12 text-center text-lg font-bold uppercase",
                      cell === BLACK_CELL &&
                        "bg-black text-white"
                    )}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (gridSize === 0) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No crossword available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-5xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Crossword Puzzle
        </CardTitle>
        <CardDescription>
          Fill in the crossword using the clues.
        </CardDescription>
      </CardHeader>

      <CardContent className="grid md:grid-cols-2 gap-10">
        {/* Grid */}
        <div className="flex justify-center">
          <div
            className="grid gap-0.5 bg-muted/60 p-4 rounded-xl"
            style={{
              gridTemplateColumns: `repeat(${gridSize}, 50px)`,
            }}
          >
            {userGrid.map((row, rIdx) =>
              row.map((cell, cIdx) => {
                const black = isBlackCell(rIdx, cIdx);
                const active = isActiveCell(rIdx, cIdx);
                const clueNumber = getClueNumber(rIdx, cIdx);
                const correct =
                cell.toUpperCase() ===
                grid[rIdx][cIdx].toUpperCase();

                return (
                  <div
                    key={`${rIdx}-${cIdx}`}
                    className="relative"
                  >
                    {clueNumber && (
                      <span className="absolute top-0 left-0 text-[10px] font-bold text-muted-foreground">
                        {clueNumber}
                      </span>
                    )}

                    {black ? (
                      <div className="w-12 h-12 bg-black rounded-sm" />
                    ) : (
                      <Input
                        maxLength={1}
                        value={cell}
                        disabled={!active || showResults}
                        onChange={e =>
                          handleStudentChange(
                            rIdx,
                            cIdx,
                            e.target.value
                          )
                        }
                        className={cn(
                          "w-12 h-12 text-center text-xl font-bold uppercase transition-all duration-200",
                          !active && "bg-muted cursor-not-allowed",

                          // AFTER CONTINUE - CORRECT
                          showResults && active && correct &&
                            "bg-green-100 border-green-300",

                          // AFTER CONTINUE - WRONG
                          showResults && active && !correct &&
                            "bg-red-100 border-red-300",

                          // Disable visual interaction after result
                          showResults && active && "cursor-not-allowed opacity-95"
                        )}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Clues */}
        <div className="space-y-8">
          <div>
            <h3 className="font-semibold mb-3">Across</h3>
            <ul className="space-y-1 text-sm">
              {clues.across
                .sort((a, b) => a.number - b.number)
                .map(c => (
                  <li key={c.number}>
                    <strong>{c.number}.</strong>{" "}
                    {c.clue}
                  </li>
                ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-3">Down</h3>
            <ul className="space-y-1 text-sm">
              {clues.down
                .sort((a, b) => a.number - b.number)
                .map(c => (
                  <li key={c.number}>
                    <strong>{c.number}.</strong>{" "}
                    {c.clue}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
